import type { RepoState } from "../lib/types";

interface Props {
  repo: RepoState;
  /** Called when the user clicks "Recover" on a reflog entry. Index is HEAD@{N}. */
  onRecover: (reflogIndex: number) => void;
  /** Highlight the currently active HEAD commit. */
  currentCommitId?: string;
}

/**
 * Right-pane panel for the reflog step. Shows every HEAD movement, newest first,
 * with a Recover button to `git reset --hard HEAD@{N}` to that point.
 */
export function Reflog({ repo, onRecover, currentCommitId }: Props) {
  const entries = repo.reflog ?? [];
  // Newest first: HEAD@{0} is the last entry.
  const display = [...entries].reverse();

  return (
    <div className="gf-reflog">
      <div className="gf-reflog__head">
        <div>
          <h3>git reflog</h3>
          <div className="gf-reflog__sub">
            Every HEAD movement, newest first. Click <b>Recover</b> on any row
            to <code>git reset --hard HEAD@{`{N}`}</code> to that point.
          </div>
        </div>
        <div className="gf-reflog__count">
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
        </div>
      </div>

      <div className="gf-reflog__list">
        {display.length === 0 && (
          <div className="gf-reflog__empty">
            No reflog entries yet. Make a commit or reset to populate.
          </div>
        )}
        {display.map((entry, displayIdx) => {
          const isCurrent = entry.commitId === currentCommitId && displayIdx === 0;
          return (
            <div
              key={`${displayIdx}-${entry.commitId}`}
              className={`gf-reflog__row${isCurrent ? " is-current" : ""}`}
            >
              <div className="gf-reflog__ref">
                <code>HEAD@{`{${displayIdx}}`}</code>
              </div>
              <div className="gf-reflog__meta">
                <div className="gf-reflog__sha">{entry.commitId.slice(0, 7)}</div>
                <div className="gf-reflog__action">{entry.action}</div>
              </div>
              <div className="gf-reflog__action-col">
                {isCurrent ? (
                  <span className="gf-reflog__current">current</span>
                ) : (
                  <button
                    className="gf-btn gf-btn--secondary gf-reflog__recover"
                    onClick={() => onRecover(displayIdx)}
                    title={`git reset --hard HEAD@{${displayIdx}}`}
                  >
                    Recover
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
